import { useCachedSearchResults } from "@catchy/app-core/src/useCachedSearchResults";
import { fetchStopSearchResults, getStopSearchResourceState } from "@catchy/app-core/src/stopSearchResource";
import { getStoredStopId } from "@catchy/app-core/src/stopIds";
import { favouriteDetailsKey } from "@catchy/app-core/src/favouriteStopDetails";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import SearchIcon from "@catchy/assets/svgs/search.svg";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
import { compareStopsByDistance, getPreferredStopName, getPublicStopIdentifier, normalizeStopDescription, matchesStopSearch, getStopSearchMatchRank, useGrantedGeolocation } from "@catchy/app-core";
import IconButton from "./IconButton";
import SearchResultIcon from "./SearchResultIcon";
import { getStopTypeColorClass } from "./StopRow";
const resolveDefaultStopType = (stop, type) => stop.stop_type || type;
export default function StopSearch({
  stops,
  type,
  preferredStopNames,
  isLoading: suppliedLoading,
  searchState: suppliedSearchState = "idle",
  onRetry: suppliedRetry,
  searchType = type,
  useLocalStorage,
  favouritesStorageKey,
  fallbackLocation,
  resolveStopType = resolveDefaultStopType,
  showStopIdentifier = () => true,
  placeholder = type === "train" ? "Search train stations" : "Search stops"
}) {
  const [selectedSearch, setSelectedSearch] = useState("");
  const [query, setQuery] = useState("");
  const [navigating, setNavigating] = useState(false);
  const inputRef = useRef();
  const router = useRouter();
  const [favourites] = useLocalStorage(favouritesStorageKey, []);
  const {
    location,
    requestGrantedLocation
  } = useGrantedGeolocation();
  const searchOrigin = location || fallbackLocation;
  const normalizedQuery = query.trim().toLowerCase();
  const [cachedDetails] = useLocalStorage(favouriteDetailsKey, {});
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(normalizedQuery), 180);
    return () => clearTimeout(timer);
  }, [normalizedQuery]);
  const remoteSearch = !Array.isArray(stops);
  const readyToSearch = normalizedQuery && normalizedQuery === debouncedQuery;
  const searchUrl = remoteSearch && normalizedQuery ? `/api/stops/search?type=${encodeURIComponent(searchType)}&query=${encodeURIComponent(normalizedQuery)}&limit=20&lat=${searchOrigin?.latitude ?? ""}&lng=${searchOrigin?.longitude ?? ""}` : null;
  const {
    data,
    error,
    isLoading: remoteLoading,
    mutate
  } = useCachedSearchResults(searchUrl, fetchStopSearchResults, readyToSearch);
  const isLoading = remoteSearch ? remoteLoading : suppliedLoading;
  const searchState = remoteSearch ? getStopSearchResourceState({
    data,
    error,
    isLoading
  }) : suppliedSearchState;
  const onRetry = remoteSearch ? () => mutate() : suppliedRetry;
  // Locally renamed favourites must remain searchable even when their default
  // GTFS name doesn't match the server query. Match the exact saved stop ID.
  const searchStops = useMemo(() => {
    if (!remoteSearch) return stops;
    const rows = new Map((data || []).map(stop => [stop.stop_id, stop]));
    const localStops = new Map(Object.values(cachedDetails || {}).filter(stop_0 => stop_0?.stop_id).map(stop_1 => [stop_1.stop_id, stop_1]));
    for (const renamed of preferredStopNames || []) {
      const id = getStoredStopId(renamed);
      if (id && !localStops.has(id)) localStops.set(id, {
        stop_id: id,
        stop_name: `Stop ${id}`
      });
    }
    for (const stop_2 of localStops.values()) {
      if (!stop_2?.stop_id || rows.has(stop_2.stop_id)) continue;
      const stopType = resolveStopType(stop_2, type);
      if (searchType === "all" || stopType === searchType || searchType === "cablecar" && stopType === "cable_car") rows.set(stop_2.stop_id, stop_2);
    }
    return [...rows.values()];
  }, [remoteSearch, stops, data, cachedDetails, resolveStopType, searchType, preferredStopNames, type]);
  const hasQuery = Boolean(normalizedQuery);
  const preparedStops = useMemo(() => (hasQuery ? searchStops || [] : []).map(stop_3 => ({
    ...stop_3,
    preferred_name: getPreferredStopName(preferredStopNames, stop_3.stop_id, stop_3.stop_code),
    stop_description: normalizeStopDescription(stop_3.stop_description),
    type: "stop"
  })), [searchStops, preferredStopNames, hasQuery]);
  const searchResults = query.trim() ? preparedStops.filter(stop_4 => matchesStopSearch(stop_4, query)).sort((a, b) => {
    const rankDifference = getStopSearchMatchRank(a, normalizedQuery) - getStopSearchMatchRank(b, normalizedQuery);
    if (rankDifference) return rankDifference;
    const distanceDifference = compareStopsByDistance({
      a,
      b,
      ...searchOrigin
    });
    return distanceDifference || (a.preferred_name || a.stop_name).localeCompare(b.preferred_name || b.stop_name);
  }).slice(0, 20) : [];
  return <div className="relative px-[20px]">
      <Combobox as="div" value={selectedSearch || query} onChange={stop_5 => {
      if (!stop_5?.stop_id) return;
      setSelectedSearch(stop_5.preferred_name || stop_5.stop_name);
      setNavigating(true);
      router.push(`/stop/${stop_5.stop_id}`);
    }} className="relative mb-2 pt-2 text-lg">
        <div className="flex items-center rounded-lg bg-white shadow-md ring-1 ring-zinc-200 transition-all focus-within:shadow-lg dark:bg-black dark:shadow-none dark:ring-zinc-500">
          <div className="grid w-14 shrink-0 place-items-center">
            <SearchIcon className="left-[calc(20px+0.75rem)] top-[1.375rem] z-10 size-6" title="Search icon" />
          </div>
          <ComboboxInput onChange={event => {
          requestGrantedLocation();
          setQuery(event.target.value);
        }} className="w-full appearance-none bg-transparent py-3 placeholder-zinc-400 outline-none" ref={inputRef} placeholder={placeholder} autoCorrect="off" autoComplete="off" autoCapitalize="off" spellCheck="false" enterKeyHint="go" aria-label="Search by stop name or number" />
          {query.trim() && !navigating ? <IconButton onClick={() => {
          inputRef.current.focus();
          setQuery("");
        }} label="Clear search" variant="subtle" className="right-[calc(20px+.75rem)] top-4 z-10 mr-2">
              <CloseCross aria-hidden="true" className="h-[1.125rem] w-[1.125rem] text-zinc-500 dark:text-zinc-300" />
            </IconButton> : null}
        </div>
        <ComboboxOptions modal={false} className={`absolute z-30 mt-1 max-h-[calc(var(--vvh)-6.25rem)] w-full max-w-full overflow-y-auto overscroll-none rounded-lg bg-white shadow-lg ring-1 ring-zinc-100 dark:bg-black dark:shadow-none dark:ring-zinc-500 ${query ? "visible" : "invisible"}`}>
          {searchState === "loading" || isLoading && searchState === "idle" ? <div className="relative cursor-default select-none px-14 py-4 leading-tight opacity-70">
              Loading stops ...
            </div> : null}
          {searchState === "unavailable" ? <div className="relative flex items-center justify-between gap-3 px-14 py-4 leading-tight opacity-70">
              <span>Stops are unavailable. Try again.</span>
              <button type="button" onClick={onRetry} className="font-semibold text-blue-600 dark:text-blue-400">
                Retry
              </button>
            </div> : null}
          {searchState !== "loading" && !(isLoading && searchState === "idle") && searchState !== "unavailable" && searchResults.length === 0 && query ? <div className="relative cursor-default select-none px-14 py-4 leading-tight opacity-70">
              No matches for <span className="font-semibold">{query}</span>
            </div> : null}
          {searchState === "refresh-error" ? <div className="relative flex items-center justify-between gap-3 px-14 py-3 text-sm leading-tight opacity-70">
              <span>Results may be out of date.</span>
              <button type="button" onClick={onRetry} className="font-semibold text-blue-600 dark:text-blue-400">
                Retry
              </button>
            </div> : null}
          {searchState !== "loading" && !(isLoading && searchState === "idle") && searchState !== "unavailable" ? searchResults.map(stop_6 => {
          const identifierMatches = showStopIdentifier(stop_6, type) && (stop_6.stop_code?.includes(query) || stop_6.stop_id.includes(query));
          const description = stop_6.stop_description;
          return <ComboboxOption key={stop_6.stop_id} value={stop_6} as="div" className="pl-2 hover:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 data-[selected]:bg-zinc-400/10">
                    <div className="items-start flex flex-nowrap py-4 pr-3 leading-tight transition-all duration-200">
                      <SearchResultIcon>
                        <StopMarker className={`${favourites?.includes(stop_6.stop_id) ? getStopTypeColorClass(resolveStopType(stop_6, type)) : "text-zinc-300 dark:text-zinc-500"} mt-0.5 h-[1.125rem] w-3 flex-shrink-0`} />
                      </SearchResultIcon>
                      <div className="flex flex-col gap-y-1">
                        <div>{stop_6.preferred_name || stop_6.stop_name}</div>
                        {stop_6.preferred_name ? <div className="text-sm leading-none opacity-80">
                            {stop_6.stop_name}
                          </div> : null}
                        {identifierMatches || description ? <div className="text-sm leading-none opacity-60">
                            {identifierMatches ? `Stop ${getPublicStopIdentifier(stop_6)}` : null}
                            {identifierMatches && description ? " " : null}
                            {description}
                          </div> : null}
                      </div>
                    </div>
                  </ComboboxOption>;
        }) : null}
        </ComboboxOptions>
      </Combobox>
    </div>;
}